import React from 'react'
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
  useTheme,
} from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { alpha } from '@mui/material/styles'
import { ConfigToggleProps } from '../types/EnvironmentConfig.types'

type PaletteColorKey = 'primary' | 'secondary' | 'success' | 'warning' | 'error'

/**
 * ConfigToggle Component
 * A reusable toggle switch component for boolean settings
 */
const ConfigToggle: React.FC<ConfigToggleProps> = ({
  label,
  value,
  onChange,
  description,
  disabled = false,
  color = 'primary',
}) => {
  const theme = useTheme()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked)
  }

  const paletteKey = color as PaletteColorKey
  const accent =
    theme.palette[paletteKey]?.main ?? theme.palette.primary.main
  const accentHoverBg = alpha(accent, 0.12)

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={value}
                onChange={handleChange}
                disabled={disabled}
                color={color}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: accent,
                    '&:hover': {
                      backgroundColor: accentHoverBg,
                    },
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: accent,
                  },
                  '& .MuiSwitch-switchBase.Mui-disabled': {
                    color: alpha(theme.palette.text.primary, 0.26),
                  },
                  '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                    backgroundColor: alpha(theme.palette.text.primary, 0.12),
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: disabled ? 'text.disabled' : 'text.primary',
                    display: 'flex',
                    alignItems: 'center',
                    fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                  }}
                >
                  {label}
                </Typography>
                {description && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: disabled ? 'text.disabled' : 'text.secondary',
                      mt: 0.5,
                      lineHeight: 1.6,
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                    }}
                  >
                    {description}
                  </Typography>
                )}
              </Box>
            }
            sx={{
              margin: 0,
              alignItems: 'flex-start',
              '& .MuiFormControlLabel-label': {
                marginTop: 0.5,
              },
            }}
          />
        </Box>

        {description && (
          <Tooltip
            title={description}
            placement="top"
            arrow
            sx={{
              maxWidth: 300,
            }}
          >
            {disabled ? (
              <span>
                <IconButton
                  size="small"
                  disabled={disabled}
                  sx={{
                    ml: 2,
                    color: 'text.disabled',
                  }}
                >
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            ) : (
              <IconButton
                size="small"
                disabled={disabled}
                sx={{
                  ml: 2,
                  color: 'text.secondary',
                  '&:hover': {
                    color: accent,
                    bgcolor: accentHoverBg,
                  },
                }}
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Tooltip>
        )}
      </Box>
    </Box>
  )
}

export default ConfigToggle
